import React from "react";
import LegalLayout, { H2, P, UL } from "@/components/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="February 24, 2026">
      <P>
        Welcome to ContentOS. These Terms of Service ("Terms") govern your access to and use of the ContentOS website, application, APIs, and related services (collectively, the "Service") provided by <strong>ContentOS Digital LLC</strong> ("ContentOS", "we", "us"). By creating an account or using the Service, you agree to these Terms.
      </P>

      <H2>1. Who Can Use ContentOS</H2>
      <P>
        You must be at least 18 years old and legally able to enter into a binding contract in your jurisdiction. If you are using the Service on behalf of a company or other entity, you represent that you have authority to bind that entity to these Terms.
      </P>

      <H2>2. Your Account</H2>
      <P>
        You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately at <a className="text-primary underline" href="mailto:support@contentos.digital">support@contentos.digital</a> if you suspect unauthorized access.
      </P>

      <H2>3. What ContentOS Provides</H2>
      <P>
        ContentOS is an AI-powered organic marketing assistant. Given information you provide about your business, offer, customer, and voice, ContentOS generates strategic content plans, hooks, scripts, captions, calls to action, performance analyses, and related outputs. ContentOS does <em>not</em> publish content on your behalf, does not guarantee any particular marketing outcome, and is not a substitute for professional marketing, legal, tax, or business advice.
      </P>

      <H2>4. Your Content</H2>
      <P>
        You retain ownership of all information, files, transcripts, testimonials, and other materials you submit ("Your Content"). You grant ContentOS a worldwide, non-exclusive, royalty-free license to process Your Content solely to operate, improve, and provide the Service to you.
      </P>
      <P>
        You are solely responsible for Your Content and for any content you publish based on ContentOS outputs. You represent that you have all necessary rights to Your Content and that it does not infringe any third-party rights, violate any law, or contain personal data of third parties that you are not authorized to share.
      </P>

      <H2>5. AI-Generated Output</H2>
      <UL>
        <li>Outputs generated by ContentOS ("Output") are provided "as is" — you must review, edit, and approve any Output before publishing.</li>
        <li>Similar Outputs may be generated for other customers with similar inputs. Nothing in these Terms grants you exclusive rights over any Output text.</li>
        <li>Subject to your compliance with these Terms and applicable laws, you may use Output for your own commercial purposes.</li>
      </UL>

      <H2>6. Subscription, Billing, and Cancellation</H2>
      <P>
        Paid access to ContentOS is provided via monthly subscriptions billed in U.S. dollars. Our payment processor, <strong>Paddle.com Market Limited</strong> ("Paddle"), acts as the <strong>Merchant of Record</strong> and authorised reseller for these Transactions. This means Paddle — not ContentOS — is the counterparty to your purchase for payment, tax, invoicing, and refund purposes.
      </P>
      <P>
        By subscribing, you enter into a purchase agreement with Paddle governed by the{" "}
        <a className="text-primary underline" href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noreferrer">Paddle Buyer Terms and Conditions</a>, and you authorize Paddle to charge your payment method on a recurring basis until you cancel. Prices displayed on the pricing page are exclusive of any applicable sales, use, or value-added taxes, which Paddle will collect where required and remit to the relevant tax authority. Paddle will issue your receipt and any invoices.
      </P>
      <P>
        <strong>Cancellation.</strong> You may cancel your subscription at any time by (a) using the "Manage Subscription" link in your Paddle receipt email, (b) visiting{" "}
        <a className="text-primary underline" href="https://paddle.net" target="_blank" rel="noreferrer">paddle.net</a>, or (c) from within the Service. Cancellation takes effect at the end of your current billing cycle; you retain access until that date.
      </P>
      <P>
        <strong>Refunds.</strong> Because Paddle is the Merchant of Record, all refund and withdrawal requests are handled by Paddle in accordance with the <a className="text-primary underline" href="/refunds">Refund Policy</a> (which incorporates the Paddle Refund Policy). To request a refund, use the "Request refund" option at{" "}
        <a className="text-primary underline" href="https://paddle.net" target="_blank" rel="noreferrer">paddle.net</a> or the link in your receipt. Statutory refund and withdrawal rights available under your local consumer protection laws are not affected by these Terms.
      </P>

      <H2>7. Acceptable Use</H2>
      <P>You agree not to:</P>
      <UL>
        <li>Use the Service to generate content that is illegal, defamatory, harassing, deceptive, or that infringes intellectual property or privacy rights.</li>
        <li>Attempt to reverse-engineer, decompile, or extract underlying models from the Service.</li>
        <li>Resell, sublicense, or provide access to the Service to third parties as part of a competing product.</li>
        <li>Use automated scripts to scrape, crawl, or generate Output at volumes that materially exceed typical human use.</li>
        <li>Bypass authentication or attempt to access accounts, data, or systems you are not authorized to access.</li>
      </UL>

      <H2>8. Third-Party Services</H2>
      <P>
        The Service integrates with third parties, including <strong>Anthropic</strong> (AI model provider), <strong>Paddle</strong> (Merchant of Record and payment processor), <strong>Meta</strong>, <strong>LinkedIn</strong>, and <strong>Google</strong>. Your use of those services is governed by their own terms and privacy policies. In particular, your purchase Transaction is subject to the{" "}
        <a className="text-primary underline" href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noreferrer">Paddle Buyer Terms</a> and the{" "}
        <a className="text-primary underline" href="/refunds">Refund Policy</a>. We are not responsible for third-party services.
      </P>

      <H2>9. Intellectual Property</H2>
      <P>
        The Service, including its software, design, and trademarks, is owned by ContentOS and protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable license to access and use the Service in accordance with these Terms.
      </P>

      <H2>10. Termination</H2>
      <P>
        We may suspend or terminate your account at our discretion if you materially breach these Terms, if we are required to do so by law, or to protect the Service or its users. On termination, your right to use the Service ends immediately. Sections that by their nature should survive termination will do so.
      </P>

      <H2>11. Disclaimers</H2>
      <P>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT OUTPUT WILL BE ACCURATE, RELIABLE, OR SUITABLE FOR YOUR PURPOSE.
      </P>

      <H2>12. Limitation of Liability</H2>
      <P>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, CONTENTOS'S TOTAL LIABILITY UNDER THESE TERMS WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE 3 MONTHS PRECEDING THE CLAIM, OR (B) US $100. WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS OR REVENUES.
      </P>

      <H2>13. Indemnification</H2>
      <P>
        You will defend and indemnify ContentOS against any claim arising from Your Content, your use of Output, or your breach of these Terms.
      </P>

      <H2>14. Changes to the Terms</H2>
      <P>
        We may update these Terms from time to time. If we make material changes, we will notify you (for example, by email or in-app notice) at least 14 days before the changes take effect. Your continued use of the Service after the effective date constitutes acceptance.
      </P>

      <H2>15. Governing Law</H2>
      <P>
        These Terms are governed by the laws of <strong>Tennessee, USA</strong>, without regard to conflict-of-laws principles. Any dispute will be resolved in the courts located in that jurisdiction, unless applicable law grants you additional consumer protections.
      </P>

      <H2>16. Contact</H2>
      <P>
        Questions about these Terms: <a className="text-primary underline" href="mailto:support@contentos.digital">support@contentos.digital</a>.
      </P>
    </LegalLayout>
  );
}
